import { useCallback } from 'react';

import { notifications } from '@mantine/notifications';

import type { TProject } from '@newo/api/gateway';
import { useSleep } from '@/hooks/useSleep.ts';
import {
    useCreateProjectLegacyMutation,
    useProjectsQuery,
    useDeleteProjectLegacyMutation,
    useUpdateProjectLegacyMutation,
    useCreateProjectImageMutation,
    useForceUpdateProjectMutation
} from '@/services/designer/projects/projects.queries';
import { openConfirmDeleteModal } from '@/utils/ui';

export const useProjects = () => {
    const {
        data: projects,
        isLoading: isProjectsLoading,
        refetch
    } = useProjectsQuery();
    const { sleep } = useSleep();
    const DELAY_REFETCH_REQUEST = 3000;

    const { mutateAsync: createProject } = useCreateProjectLegacyMutation({
        onSuccess: () => {
            refetch();
        }
    });
    const { mutateAsync: deleteProject } = useDeleteProjectLegacyMutation({
        onSuccess: () => {
            refetch();
        }
    });
    const { mutateAsync: updateProject } = useUpdateProjectLegacyMutation({
        onSuccess: () => {
            refetch();
        }
    });
    const { mutateAsync: createProjectImage } = useCreateProjectImageMutation({
        onSuccess: () =>
            notifications.show({
                title: 'Project Image Created',
                message: 'Project image created successfully',
                color: 'green'
            })
    });
    const { mutateAsync: forceUpdateProject } = useForceUpdateProjectMutation({
        onSuccess: async () => {
            notifications.show({
                title: 'Project Updated',
                message: 'Project updated successfully',
                color: 'green'
            });
            await sleep(DELAY_REFETCH_REQUEST);
            refetch();
        }
    });

    const handleDelete = useCallback(
        async (project: TProject) => {
            openConfirmDeleteModal({
                title: `Delete ${project.idn} project?`,
                children: (
                    <>Are you sure you want to delete {project.idn} project?</>
                ),
                confirmLabel: 'Delete Project',
                onConfirm: () => {
                    deleteProject(
                        { projectId: project.id },
                        {
                            onSuccess: () => {
                                refetch();
                            }
                        }
                    );
                }
            });
        },
        [deleteProject, refetch]
    );

    return {
        projects,
        isProjectsLoading,
        onProjectCreate: createProject,
        onProjectDelete: handleDelete,
        onProjectUpdate: updateProject,
        onProjectImageCreate: createProjectImage,
        onProjectForceUpdate: forceUpdateProject
    };
};
